<template>
  <page no-styling style="overflow: hidden;" class="pb4">
    <v-touch
      @swipeleft="next()"
      @swiperight="previous()"
    >
      <div class="card py0">
        <div class="month-nav">

          <div
            v-if="dayIsSelected || !isCurrentMonth"
            class="delete mx1"
            @click="goToCurrentMonth"
            style="max-width: 100px"
          >
            {{ $t('cms.statistics.GoToCurrentMonth') }}
          </div>
          <span v-else class="bold mya black ml2">
            {{ $t('cms.statistics.Header') }}
          </span>
          <!-- todo: get the svgs of arrow-left and -right in the center of that component-->
          <!-- And remove the padding-top of the arrows here-->
          <div class="flex" style="align-items: center">
            <button
              @click="previous()"
              type="button"
              class="no-button"
              style="padding-top: 3px"
            >
              <arrow-left class="mya action black"/>
            </button>

            <transition :name="animate">
              <h4
                v-show="displayCalendar"
                class="current-date"
                @click="openCalendar"
              >
                {{ selectedDateText }}
              </h4>
            </transition>

            <button
              @click="next()"
              type="button"
              class="no-button"
              style="padding-top: 3px"
            >
              <arrow-right class="mya action black"/>
            </button>
          </div>
        </div>

        <transition v-if="stats" :name="animate">
          <Metrics
            v-show="displayCalendar"
            :stats="stats"
            class="mt4"
          />
        </transition>

        <modal
          v-model="showCalendarModal"
          class="no-background"
          style="max-height: 90vh;"
        >
          <div>
            <card-header style="margin-bottom: 0;">
              <template #icon>
                <icon statsAgenda xlarge />
              </template>
              <template #subtitle>
                <div class="flex align_center justify_center">
                  <button
                    @click="previous()"
                    type="button"
                    class="no-button"
                    style="padding-top: 3px"
                  >
                    <arrow-left class="mya action black"/>
                  </button>

                  <transition :name="animate">
                    <h4
                      v-show="displayCalendar"
                      class="current-date-modal"
                    >
                      {{ selectedDateText }}
                    </h4>
                  </transition>

                  <button
                    @click="next()"
                    type="button"
                    class="no-button"
                    style="padding-top: 3px"
                  >
                    <arrow-right class="mya action black"/>
                  </button>
                </div>
              </template>
              <div class="flex justify_between mb2" style="padding: 0 34px;">
                <div
                  v-for="(weekDay, dayNumber) in $CONSTANTS.shortWeekdays"
                  :key="dayNumber"
                  class="center_text"
                >
                  <span><b>{{ weekDay }}</b></span>
                </div>
              </div>
            </card-header>
          </div>

          <div class="flex justify_between px2 slightly_yellow_background relative">
            <div class="calendar-line"></div>
            <div
              v-for="(weekDay, dayNumber) in $CONSTANTS.shortWeekdays"
              :key="dayNumber"
              class="flex column center_text relative"
            >
              <div
                v-for="weekNumber in amountOfWeeksInCurrentMonth"
                :key="weekNumber"
              >
                <day-in-calendar
                  :dayData="getDayData(dayNumber, weekNumber - 1)"
                  v-model="selectedDay"
                />
              </div>
            </div>
          </div>
          <div class="calendar-footer">
            <icon hihelloLogo />
            <p class="ml2">
              {{ $t('cms.statistics.CalenderDescription') }}
            </p>
          </div>
        </modal>
      </div>
    </v-touch>
    <div class="card-spacing" />

    <div class="card slightly_yellow_background">
      <card-header
        :title="translatedTitle"
        :description="translatedDescription"
      >
        <template #icon>
          <icon :iconName="couponIcon" xlarge />
        </template>
        <template #info>
          TODO: replace info modal here
        </template>
      </card-header>
      <div class="px1">
        <select-dropdown
          :options="Object.values($CONSTANTS.couponStatisticsTypes)"
          v-model="typeFilter"
        />
      </div>
    </div>

    <div
      :class="{loading: !fetchedDataForDate}"
      v-show="displayCalendar"
    >
      <loader v-if="!fetchedDataForDate" style="height: 200px"/>
      <coupon-list
        v-else-if="coupons.length > 0"
        :coupons="coupons"
        show-stats
      />
      <h4 v-else>{{ $t('cms.coupon.NoneAvailable') }}</h4>
    </div>
  </page>
</template>

<script>
import omit from 'lodash/omit';
import range from 'lodash/range';
import format from 'date-fns/format';
import DayInCalendar from '@/components/statistics/DayInCalendar.vue';
import Metrics from '@/components/Metrics.vue';
import CouponList from '@/components/CouponList.vue';
import SelectDropdown from '@/components/SelectDropdown.vue';
import CardHeader from '@/components/global/CardHeader.vue';
import Icon from '@/components/global/Icon.vue';

export default {
  name: 'statistics',

  components: {
    CouponList,
    Metrics,
    DayInCalendar,
    SelectDropdown,
    CardHeader,
    Icon,
  },

  data() {
    return {
      // For animation:
      animate: 'slide',
      displayCalendar: true,

      // Set by user
      selectedMonthIndex: null, // This is backwards :D 1 will be one month before current month.
      typeFilter: this.$CONSTANTS.couponStatisticsTypes.all.value,
      selectedDay: this.$MODELS.selectedDay,
      showCalendarModal: false,

      // to display calendar:
      today: new Date(),
      months: [], // contains everything to construct calendar. including 'day.isSelectable'

      // fetched data
      fetchedDayData: false,
      fetchedDataForDate: false,
      stats: this.$MODELS.stats,
      availableDayData: [],
      coupons: [],
    };
  },

  computed: {
    dayIsSelected() {
      return !!(this.selectedDay && this.selectedDay.yearNumber);
    },

    selectedMonth() {
      return this.months[this.selectedMonthIndex];
    },

    isCurrentMonth() {
      return this.selectedMonthIndex === 0;
    },

    nextIsAvailable() {
      return this.selectedMonthIndex !== 0;
    },

    selectedDateText() {
      const dayStr = this.selectedDay.dayNumber ? `${this.selectedDay.dayNumber} ` : '';
      return `${dayStr}${this.selectedMonth.name} ${this.selectedMonth.yearNumber}`;
    },

    amountOfWeeksInCurrentMonth() {
      return Math.ceil((this.selectedMonth.days.length + this.selectedMonth.startsOnWeekday) / 7);
    },

    couponIcon() {
      return this.$CONSTANTS.couponStatisticsTypes[this.typeFilter.toLowerCase()].icon;
    },

    translatedTitle() {
      return this.$CONSTANTS.couponStatisticsTypes[this.typeFilter.toLowerCase()].title;
    },

    translatedDescription() {
      return this.$CONSTANTS.couponStatisticsTypes[this.typeFilter.toLowerCase()].description;
    },
  },

  watch: {
    async selectedMonthIndex(val) {
      this.selectedDay = this.$MODELS.selectedDay;
      if (this.months.length - 1 === val) {
        this.addMonth();
      }

      await this.fetchStatsAndCoupons();
    },

    typeFilter() {
      this.fetchStatsAndCoupons();
    },

    selectedDay: {
      handler(selectedDay) {
        this.fetchStatsAndCoupons();
        this.showFullCalendar = !selectedDay.dayNumber;
      },
      deep: true,
    },
  },

  created() {
    this.fetchClickableDates();

    this.months.push(this.initMonth(this.today.getFullYear(), this.today.getMonth()));
    this.addMonth();
    this.selectedMonthIndex = 0;

    this.fetchStatsAndCoupons();
  },

  methods: {
    async fetchClickableDates() {
      const { data, errors } = await this.$query(this.$queries.statsAvailable);
      const { statsAvailable } = data;

      if (errors.length > 0 || !statsAvailable) {
        this.handleErrors(errors);
      } else {
        this.fetchedDayData = true;
        this.availableDayData = statsAvailable;
        this.months.forEach(this.setSelectableDaysInMonth);
      }
    },

    async fetchStatsAndCoupons() {
      this.fetchedDataForDate = false; // set ui to loading

      // let vue do UI stuff before we block CPU with heavy requests and calculations
      await this.$nextTick();

      const year = this.selectedMonth.yearNumber;
      const month = this.selectedMonth.monthNumber;
      const day = this.selectedDay.dayNumber || 1;
      const monthly = !this.selectedDay.dayNumber;

      const statsInput = {
        date: format(new Date(year, month, day), 'YYYY-MM-DD'),
        monthly,
        typeFilter: this.typeFilter,
      };

      const { data, errors } = await this.$query(this.$queries.stats, { statsInput });
      const { stats } = data;

      if (errors.length > 0 || !stats) {
        this.handleErrors(errors);
      } else {
        this.stats = omit(stats, 'coupons');
        this.coupons = stats.coupons || [];
        this.fetchedDataForDate = true;
      }
    },

    openCalendar() {
      this.showCalendarModal = true;
    },

    closeCalendar() {
      this.showCalendarModal = false;
    },

    goToCurrentMonth() {
      this.selectedMonthIndex = 0;
      this.selectedDay = this.$MODELS.selectedDay;
    },

    setMonthIndex(direction) {
      this.animate = direction < 0 ? 'slide' : 'reversed_slide';
      this.displayCalendar = false;

      // Change calendar data after 80 ms
      setTimeout(() => {
        this.selectedMonthIndex = this.selectedMonthIndex + direction;
      }, 80);

      // Enter calendar after 120 ms
      setTimeout(() => {
        this.displayCalendar = true;
      }, 120);
    },

    next() {
      if (this.nextIsAvailable) {
        this.setMonthIndex(-1);
      }
    },

    previous() {
      if (!this.dayIsSelected) {
        this.setMonthIndex(1);
      }
    },

    setSelectableDaysInMonth(month) {
      month.days.forEach((dayData) => {
        const searchString = format(new Date(month.yearNumber, month.monthNumber, dayData.dayNumber), 'YYYY-MM-DD');
        // eslint-disable-next-line
        dayData.isSelectable = this.availableDayData.includes(searchString);
      });
    },

    getShowWeekNumber(weekNumber) {
      return this.showFullCalendar
        || !this.dayIsSelected
        || weekNumber === this.selectedDay.weekNumber + 1;
    },

    getDayData(weekDay, weekNumber) {
      const dayNumber = weekNumber * 7
        + weekDay
        - this.selectedMonth.startsOnWeekday;

      return { ...this.selectedMonth.days[dayNumber], weekNumber };
    },

    getDaysInMonth(year, month) {
      return new Date(year, month + 1, 0).getDate();
    },

    getWeekday(year, month, day) {
      return new Date(year, month, day).getDay();
    },

    getMonthName(month) {
      return this.$CONSTANTS.months[month];
    },

    initMonth(yearNumber, monthNumber) {
      const amountOfDaysInMonth = this.getDaysInMonth(yearNumber, monthNumber);
      return {
        startsOnWeekday: this.getWeekday(yearNumber, monthNumber, 1),
        days: range(1, amountOfDaysInMonth + 1).map(dayNumber => ({
          yearNumber, monthNumber, dayNumber, isSelectable: null,
        })),
        name: this.getMonthName(monthNumber),
        monthNumber,
        yearNumber,
      };
    },

    addMonth() {
      let monthYear = this.months[this.months.length - 1].yearNumber;
      let monthNumber = this.months[this.months.length - 1].monthNumber - 1;

      if (monthNumber === -1) {
        // select previous year and last month.
        monthYear -= 1;
        monthNumber = 11;
      }

      const month = this.initMonth(monthYear, monthNumber);
      this.setSelectableDaysInMonth(month);
      this.months.push(month);
    },
  },
};
</script>

<style lang="scss" scoped>
.current-date {
  background-color: black;
  border-radius: $border-radius;
  color: $color_white;
  padding: $base_spacing;
  cursor: pointer;
  width: 120px;
  text-align: center;
}

.current-date-modal {
  width: 150px;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.no-button {
  background: unset;
  border: unset;
}

.arrow-icon-wrapper {
  background-color: black !important;
  border-radius: 99px;
  border: unset;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 18px;
  width: 18px;
}

.arrow-icon {
  height: 14px !important;
  padding: 0px 3px;
  fill: white;
  margin-left: -3px;
  margin-right: -3px;
}

.disabled-arrow-icon-wrapper {
  opacity: .7;
}

.month-nav {
  background-color: $color_background_light_blue;
  margin-left: -$base_spacing * 2;
  margin-right: -$base_spacing * 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.calendar-nav {
  display: flex;
  justify-content: center;
  align-items: center;
}

.calendar-line {
  position: absolute;
  top: 0;
  left: 22px;
  width: calc(100% - 44px);
  height: 63px;
  margin: auto;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  &::before{
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    transform: translateY(200%);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
  &::after {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    transform: translateY(300%);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
}

.calendar-footer {
  position: relative;
  display: flex;
  align-items: center;
  justify-items: center;
  padding: 10px;
  border-top: 1px dashed rgba(0, 0, 0, 0.2);
  background: $color_background_card_footer;
}
</style>
